import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const PRICE_FILTER_MIN = 'filter.v.price.gte';
const PRICE_FILTER_MAX = 'filter.v.price.lte';
const PRICE_SORTS = new Set(['price-ascending', 'price-descending']);
// Per-section generation counter. Each normalize run claims the next generation;
// a later submit supersedes any in-flight run so its (now stale) results are
// discarded rather than applied to the grid.
const priceNormalizationRuns = new Map();
/** @type {Map<string, Element[]>} */
const displayedPriceItemsCache = new Map();
/** @type {Map<string, number>} */
const displayedPriceRangeMaxCache = new Map();
/** @type {Map<string, { href: string, items: Element[] }>} */
const displayedPriceItemsCacheByUrl = new Map();

/**
 * What the grid is rendered for: filters, sorting, the search query and, with numbered
 * pages, the page. Infinite scroll pushes an entry per page it appends, and stepping back
 * through those must not collapse the grid. Other params (attribution, hashes) don't count.
 * @param {URL} url
 * @returns {string}
 */
function facetStateKey(url) {
  // From the grid's own attribute: price normalization removes the infinite-scroll anchors.
  const infinite = document.querySelector('results-list .product-grid[data-pagination-type="infinite"]') != null;
  const key = new URLSearchParams();
  for (const [name, value] of url.searchParams) {
    if (name.startsWith('filter.') || name === 'sort_by' || name === SEARCH_QUERY || (name === 'page' && !infinite)) {
      key.append(name, value);
    }
  }
  return key.toString();
}

/** The facet state the grid was last rendered for; back/forward re-renders when it moves. */
let renderedFacetState = facetStateKey(new URL(window.location.href));

/**
 * Records the state a render was done for. pagination.js imports this module without the
 * asset version, which loads a second copy, so the record goes out as an event for the copy
 * that owns the history handler.
 * @param {URL} [url]
 */
function rememberRenderedFacetState(url = new URL(window.location.href)) {
  document.dispatchEvent(new CustomEvent('facets:rendered', { detail: { href: url.href } }));
}

function preserveSortParameter(parameters, url = new URL(window.location.href)) {
  if (parameters.has('sort_by')) return parameters;
  const currentSort = url.searchParams.get('sort_by');
  if (currentSort) parameters.set('sort_by', currentSort);
  return parameters;
}

function preserveFilterParametersForSorting(parameters, form, url = new URL(window.location.href)) {
  const isSortingOnlySubmission = parameters.has('sort_by') &&
    !form.querySelector('[name^="filter."]');

  if (!isSortingOnlySubmission) return parameters;

  // De-duplicate by name+value rather than name. Shopify multi-select filters serialize
  // as repeated keys (filter.v.option.color=Red&filter.v.option.color=Blue), so guarding
  // by key alone would drop every value after the first for a multi-value filter.
  const existing = new Set(
    Array.from(parameters.entries(), ([name, value]) => `${name}=${value}`),
  );

  for (const [name, value] of url.searchParams.entries()) {
    if (name.startsWith('filter.') && !existing.has(`${name}=${value}`)) {
      parameters.append(name, value);
      existing.add(`${name}=${value}`);
    }
  }

  return parameters;
}

function hasDisplayedPriceControls(url = new URL(window.location.href)) {
  return PRICE_SORTS.has(url.searchParams.get('sort_by') || '') ||
    url.searchParams.has(PRICE_FILTER_MIN) ||
    url.searchParams.has(PRICE_FILTER_MAX);
}

function displayedPriceControlHref(url = new URL(window.location.href)) {
  const normalized = new URL(url.href);
  normalized.hash = '';
  return normalized.href;
}

function formatDisplayedCents(cents) {
  if (typeof window.shopifyFormatMoney === 'function') {
    return window.shopifyFormatMoney(cents);
  }
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents);
  }
  return `$${(cents / 100).toFixed(2)}`;
}

function centsToFacetInputAmount(cents) {
  const amount = cents / 100;
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

function parsePriceFacetDollarAmount(value) {
  if (value == null || value === '') return null;
  const amount = Number(String(value).replace(',', '.'));
  return Number.isFinite(amount) ? amount : null;
}

function priceFacetSliderDollars(value, fallback = 0) {
  const amount = parsePriceFacetDollarAmount(value);
  return amount == null ? fallback : Math.round(amount);
}

function displayedPriceRangeMaxCents(items, section) {
  let prices = items
    .map(cardPriceCents)
    .filter((price) => price != null && price > 0);

  if (!prices.length && section) {
    prices = gridItemsFromSection(section)
      .map(cardPriceCents)
      .filter((price) => price != null && price > 0);
  }

  return prices.length ? Math.max(...prices) : 0;
}

function gridItemsFromSection(section) {
  return Array.from(section.querySelectorAll('.product-grid__item[data-card-display-price-cents]'));
}

function setPriceFacetHighestPricePending(highestEl, pending) {
  if (!(highestEl instanceof HTMLElement)) return;
  if (pending) {
    highestEl.hidden = true;
    highestEl.setAttribute('aria-busy', 'true');
    return;
  }
  highestEl.hidden = false;
  highestEl.removeAttribute('aria-busy');
}

function hidePendingHighestPriceLabels(section) {
  section.querySelectorAll('.price-facet__highest-price').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const nativeMax = Number(el.dataset.nativeRangeMax || 0);
    if (nativeMax <= 0) {
      setPriceFacetHighestPricePending(el, true);
    }
  });
}

function resolvePriceFacetSliderValues(facet, rangeMaxDollars, url) {
  const minInput = facet.querySelector(`input[name="${PRICE_FILTER_MIN}"]`);
  const maxInput = facet.querySelector(`input[name="${PRICE_FILTER_MAX}"]`);
  const minParam = url?.searchParams.get(PRICE_FILTER_MIN) ?? null;
  const maxParam = url?.searchParams.get(PRICE_FILTER_MAX) ?? null;
  const minFromInput = minInput instanceof HTMLInputElement ? minInput.value : '';
  const maxFromInput = maxInput instanceof HTMLInputElement ? maxInput.value : '';

  let min = priceFacetSliderDollars(minParam ?? minFromInput, 0);
  let max = priceFacetSliderDollars(maxParam ?? maxFromInput, rangeMaxDollars);

  min = Math.max(0, Math.min(min, rangeMaxDollars));
  max = Math.min(rangeMaxDollars, Math.max(max, min));

  return { min, max };
}

function updatePriceFacetElement(facet, rangeMaxCents, url) {
  if (rangeMaxCents <= 0) return;

  const rangeMaxDollars = Math.ceil(rangeMaxCents / 100);
  const formattedHighest = formatDisplayedCents(rangeMaxCents);
  const inputMaxAmount = centsToFacetInputAmount(rangeMaxCents);

  const highestEl = facet.querySelector('.price-facet__highest-price');
  if (highestEl instanceof HTMLElement) {
    setPriceFacetHighestPricePending(highestEl, false);
    highestEl.dataset.nativeRangeMax = String(rangeMaxCents);
    const template = highestEl.dataset.priceFilterTemplate;
    highestEl.textContent = template
      ? template.replace('__PRICE__', formattedHighest)
      : formattedHighest;
  }

  facet.querySelectorAll('.price-facet__range').forEach((range) => {
    if (range instanceof HTMLInputElement) range.max = String(rangeMaxDollars);
  });

  const minInput = facet.querySelector(`input[name="${PRICE_FILTER_MIN}"]`);
  const maxInput = facet.querySelector(`input[name="${PRICE_FILTER_MAX}"]`);
  if (minInput instanceof HTMLInputElement) {
    minInput.dataset.max = inputMaxAmount;
  }
  if (maxInput instanceof HTMLInputElement) {
    maxInput.dataset.max = inputMaxAmount;
    maxInput.placeholder = inputMaxAmount;
  }

  const minRange = facet.querySelector('.price-facet__range--min');
  const maxRange = facet.querySelector('.price-facet__range--max');
  const slider = facet.querySelector('.price-facet__slider');
  if (
    minRange instanceof HTMLInputElement &&
    maxRange instanceof HTMLInputElement &&
    slider instanceof HTMLElement &&
    rangeMaxDollars > 0
  ) {
    const { min, max } = resolvePriceFacetSliderValues(facet, rangeMaxDollars, url);
    minRange.value = String(min);
    maxRange.value = String(max);
    slider.style.setProperty('--min', `${(min / rangeMaxDollars) * 100}%`);
    slider.style.setProperty('--max', `${(max / rangeMaxDollars) * 100}%`);
  }

  const facetStatus = facet.querySelector('[data-range-max]');
  if (facetStatus instanceof HTMLElement) {
    facetStatus.dataset.rangeMax = String(rangeMaxCents);
  }
}

function updateDisplayedPriceFacetRange(section, rangeMaxCents, url, sectionId) {
  if (rangeMaxCents <= 0) return;
  const resolvedSectionId = sectionId ||
    section.querySelector('results-list[section-id]')?.getAttribute('section-id') ||
    '';
  if (resolvedSectionId) {
    displayedPriceRangeMaxCache.set(resolvedSectionId, rangeMaxCents);
  }
  section.querySelectorAll('price-facet-component').forEach((facet) => {
    updatePriceFacetElement(facet, rangeMaxCents, url);
  });
}

function restoreDisplayedPriceFacetRangeSync(sectionId, url) {
  const section = sectionElement(sectionId);
  if (!section?.querySelector('price-facet-component')) return;

  let rangeMaxCents = displayedPriceRangeMaxCache.get(sectionId) || 0;
  if (!rangeMaxCents) {
    const cachedItems = displayedPriceItemsCache.get(sectionId);
    if (cachedItems?.length) {
      rangeMaxCents = displayedPriceRangeMaxCents(cachedItems, section);
    }
  }
  if (!rangeMaxCents) {
    rangeMaxCents = displayedPriceRangeMaxCents([], section);
  }

  if (rangeMaxCents > 0) {
    updateDisplayedPriceFacetRange(section, rangeMaxCents, url, sectionId);
    return;
  }

  hidePendingHighestPriceLabels(section);
}

function syncDisplayedSortFromUrl(section, url) {
  const sortBy = url.searchParams.get('sort_by');
  if (!sortBy) return;

  section.querySelectorAll('sorting-filter-component').forEach((sortFilter) => {
    let optionName = '';

    sortFilter.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const isSelected = input.value === sortBy;
      input.checked = isSelected;
      if (isSelected) optionName = input.dataset.optionName || optionName;
    });

    sortFilter.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      select.value = sortBy;
      const selectedOption = select.selectedOptions[0];
      if (selectedOption) {
        optionName = selectedOption.textContent?.trim() || optionName;
      }
    });

    sortFilter.querySelectorAll('[role="option"]').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      const input = option.querySelector('input[name="sort_by"]');
      const isSelected = input instanceof HTMLInputElement && input.value === sortBy;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      option.tabIndex = isSelected ? 0 : -1;
      if (isSelected && !optionName) {
        optionName = option.querySelector('.sorting-filter__label')?.textContent?.trim() || optionName;
      }
    });

    if (optionName) {
      sortFilter.querySelectorAll('.facets__summary .facets__label, .facets__summary .facets__status').forEach((el) => {
        el.textContent = optionName;
      });
    }
  });
}

function syncDisplayedPriceFacetFromUrl(section, url) {
  const minParam = url.searchParams.get(PRICE_FILTER_MIN);
  const maxParam = url.searchParams.get(PRICE_FILTER_MAX);
  const hasActivePrice = minParam != null || maxParam != null;

  section.querySelectorAll('price-facet-component').forEach((facet) => {
    const minInput = facet.querySelector(`input[name="${PRICE_FILTER_MIN}"]`);
    const maxInput = facet.querySelector(`input[name="${PRICE_FILTER_MAX}"]`);

    if (minInput instanceof HTMLInputElement) {
      minInput.value = minParam != null ? minParam : '';
    }
    if (maxInput instanceof HTMLInputElement) {
      maxInput.value = maxParam != null ? maxParam : '';
    }

    const minRange = facet.querySelector('.price-facet__range--min');
    const maxRange = facet.querySelector('.price-facet__range--max');
    const slider = facet.querySelector('.price-facet__slider');
    if (
      minInput instanceof HTMLInputElement &&
      maxInput instanceof HTMLInputElement &&
      minRange instanceof HTMLInputElement &&
      maxRange instanceof HTMLInputElement &&
      slider instanceof HTMLElement
    ) {
      const rangeMax = parseInt(maxRange.getAttribute('max') || '0', 10);
      if (rangeMax > 0) {
        const { min, max } = resolvePriceFacetSliderValues(facet, rangeMax, url);
        minRange.value = String(min);
        maxRange.value = String(max);
        slider.style.setProperty('--min', `${(min / rangeMax) * 100}%`);
        slider.style.setProperty('--max', `${(max / rangeMax) * 100}%`);
      }
    }

    const statusComponent = facet.closest('details')?.querySelector('summary facet-status-component');
    if (
      statusComponent instanceof FacetStatusComponent &&
      minInput instanceof HTMLInputElement &&
      maxInput instanceof HTMLInputElement
    ) {
      if (!hasActivePrice) {
        statusComponent.clearSummary();
      } else {
        statusComponent.updatePriceSummary(minInput, maxInput);
      }
    }

    facet.querySelectorAll('facet-clear-component').forEach((clearComponent) => {
      const clearButton = clearComponent.querySelector('.facets__clear');
      if (clearButton instanceof HTMLElement) {
        clearButton.classList.toggle('facets__clear--active', hasActivePrice);
      }
      if (clearComponent instanceof HTMLElement) {
        clearComponent.tabIndex = hasActivePrice ? 0 : -1;
      }
    });
  });
}

function syncDisplayedFacetControlsFromUrl(section, url) {
  syncDisplayedSortFromUrl(section, url);
  syncDisplayedPriceFacetFromUrl(section, url);
  syncDisplayedPriceRemovePills(section, url);
}

function syncDisplayedFacetsFromBrowserUrl(sectionId) {
  const section = sectionElement(sectionId);
  if (!section) return;
  syncDisplayedFacetControlsFromUrl(section, new URL(window.location.href));
}

function buildUrlWithoutPriceFilter(url) {
  const next = new URL(url.href);
  next.searchParams.delete(PRICE_FILTER_MIN);
  next.searchParams.delete(PRICE_FILTER_MAX);
  next.hash = '';
  return next.href;
}

function formatDisplayedPriceRemoveLabel(section, sectionId, minParam, maxParam) {
  const rangeMaxCents = displayedPriceRangeMaxCache.get(sectionId) ||
    nativePriceFacetRangeMaxCents(section);

  const formatParam = (param) => {
    const cents = priceInputToCents(param);
    return cents != null ? formatDisplayedCents(cents) : '';
  };

  if (minParam != null && maxParam != null) {
    return `${formatParam(minParam)}–${formatParam(maxParam)}`;
  }
  if (minParam != null) {
    return `${formatParam(minParam)}–${formatDisplayedCents(rangeMaxCents)}`;
  }
  if (maxParam != null) {
    return `${formatDisplayedCents(0)}–${formatParam(maxParam)}`;
  }
  return '';
}

/**
 * Found by marker, not by URL: its url_to_remove goes stale as soon as another filter changes
 * without a morph, and Shopify orders those params its own way.
 */
function findServerPriceRemovePill(container) {
  const pill = container.querySelector('facet-remove-component[data-price-remove-pill]');
  return pill instanceof HTMLElement ? pill : null;
}

function getFacetRemovePillIconFragment(section) {
  const template = section.querySelector('[data-facet-remove-pill-icon-template]');
  if (template instanceof HTMLTemplateElement) {
    return template.content.cloneNode(true);
  }

  const iconRef = section.querySelector('.facets-remove__pill .svg-wrapper');
  if (iconRef) {
    const fragment = document.createDocumentFragment();
    fragment.append(iconRef.cloneNode(true));
    const srRef = section.querySelector('.facets-remove__pill .visually-hidden');
    if (srRef) fragment.append(srRef.cloneNode(true));
    return fragment;
  }

  return null;
}

function setDisplayedPriceRemovePillLabel(pill, label) {
  const icon = pill.querySelector('.svg-wrapper');
  const srOnly = pill.querySelector('.visually-hidden');
  Array.from(pill.childNodes).forEach((node) => {
    if (node === icon || node === srOnly) return;
    node.remove();
  });
  pill.insertBefore(document.createTextNode(label), icon || srOnly || null);
}

function createDisplayedPriceRemovePill(section, label, removeUrl) {
  const pill = document.createElement('facet-remove-component');
  pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill';
  pill.dataset.displayedPriceRemovePill = '';
  pill.dataset.url = removeUrl;
  pill.tabIndex = 0;
  pill.setAttribute('role', 'button');
  pill.setAttribute('on:click', '/removeFilter?form=');
  pill.setAttribute('on:keydown', '/removeFilter?form=');

  pill.append(document.createTextNode(label));

  const iconFragment = getFacetRemovePillIconFragment(section);
  if (iconFragment) {
    pill.append(iconFragment);
  } else {
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = section.querySelector('.facets-remove')?.dataset.removeLabel || 'Remove';
    pill.append(sr);
  }

  return pill;
}

function ensureDisplayedPriceRemovePillChrome(pill, section) {
  if (!(pill instanceof HTMLElement) || pill.querySelector('.svg-wrapper')) return;
  const iconFragment = getFacetRemovePillIconFragment(section);
  if (iconFragment) pill.append(iconFragment);
}

function createFacetsClearAllButton(section, container, resultsUrl) {
  const label = container.dataset.clearAllLabel || 'Clear Filters';
  const wrapper = document.createElement('facet-remove-component');
  wrapper.dataset.facetClearAll = '';
  wrapper.dataset.url = resultsUrl;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'button-unstyled facets__clear-all-link facets__clear-all active';
  button.setAttribute('ref', 'clearButton');
  button.setAttribute('on:click', '/removeFilter?form=');
  button.setAttribute('on:keydown', '/removeFilter?form=');
  button.textContent = label;
  wrapper.append(button);
  return wrapper;
}

function ensureFacetsRemoveClearAll(container, section, url) {
  if (![...url.searchParams.keys()].some((key) => key.startsWith('filter.'))) {
    container.querySelector('[data-facet-clear-all]')?.setAttribute('hidden', '');
    container.querySelector('.facets__clear-all-link')?.classList.remove('active');
    return;
  }

  const formAction = container.closest('form')?.getAttribute('action') ||
    section.querySelector('.facets__form')?.getAttribute('action');
  const clearUrl = formAction
    ? new URL(formAction, window.location.origin).href
    : new URL(url.pathname, window.location.origin).href;

  let clearWrapper = container.querySelector('[data-facet-clear-all]');
  if (!(clearWrapper instanceof HTMLElement)) {
    const referenceWrapper = section.querySelector('[data-facet-clear-all]');
    if (referenceWrapper instanceof HTMLElement) {
      clearWrapper = referenceWrapper.cloneNode(true);
      if (clearWrapper instanceof HTMLElement) {
        container.appendChild(clearWrapper);
      }
    }
  }

  if (!(clearWrapper instanceof HTMLElement)) {
    clearWrapper = createFacetsClearAllButton(section, container, clearUrl);
    container.appendChild(clearWrapper);
  }

  clearWrapper.dataset.url = clearUrl;
  clearWrapper.removeAttribute('hidden');
  clearWrapper.querySelector('.facets__clear-all-link')?.classList.add('active');
}

function syncDisplayedPriceRemovePills(section, url) {
  const minParam = url.searchParams.get(PRICE_FILTER_MIN);
  const maxParam = url.searchParams.get(PRICE_FILTER_MAX);
  const hasPriceFilter = minParam != null || maxParam != null;
  const sectionId = section.querySelector('results-list[section-id]')?.getAttribute('section-id') || '';

  section.querySelectorAll('.facets-remove').forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    const clientPill = container.querySelector('[data-displayed-price-remove-pill]');

    if (!hasPriceFilter) {
      clientPill?.remove();
      findServerPriceRemovePill(container)?.remove();
      ensureFacetsRemoveClearAll(container, section, url);
      return;
    }

    const removeUrl = buildUrlWithoutPriceFilter(url);
    const label = formatDisplayedPriceRemoveLabel(section, sectionId, minParam, maxParam);
    if (!label) return;

    const serverPill = findServerPriceRemovePill(container);
    if (serverPill) {
      clientPill?.remove();
      // Rendered for the state before the last unmorphed change: refresh what it removes and says.
      serverPill.dataset.url = removeUrl;
      setDisplayedPriceRemovePillLabel(serverPill, label);
      ensureFacetsRemoveClearAll(container, section, url);
      return;
    }

    let pill = clientPill;
    if (!(pill instanceof HTMLElement)) {
      pill = createDisplayedPriceRemovePill(section, label, removeUrl);
      const clearAllWrapper = container.querySelector('facet-remove-component .facets__clear-all-link')?.closest('facet-remove-component');
      if (clearAllWrapper) {
        container.insertBefore(pill, clearAllWrapper);
      } else {
        container.append(pill);
      }
    } else {
      pill.dataset.url = removeUrl;
      setDisplayedPriceRemovePillLabel(pill, label);
      ensureDisplayedPriceRemovePillChrome(pill, section);
    }

    ensureFacetsRemoveClearAll(container, section, url);
  });
}

function updateDisplayedPriceEmptyState(section, count) {
  section.querySelectorAll('.main-collection-grid__empty, [data-native-empty]').forEach((emptyEl) => {
    if (!(emptyEl instanceof HTMLElement)) return;
    emptyEl.hidden = count > 0;
    if (count > 0) {
      emptyEl.setAttribute('aria-hidden', 'true');
    } else {
      emptyEl.removeAttribute('aria-hidden');
    }
  });
}

function updateFacetsSeeResultsButtons(section, count) {
  section.querySelectorAll('.facets__see-results').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const oneLabel = button.dataset.seeItemsOne;
    const otherLabel = button.dataset.seeItemsOther;
    if (oneLabel && otherLabel) {
      const template = count === 1 ? oneLabel : otherLabel;
      button.textContent = template.replace(/\d+/, String(count));
      return;
    }
    button.textContent = count === 1 ? `See ${count} item` : `See ${count} items`;
  });
}

function countDisplayedPriceItems(items, minCents, maxCents) {
  const seenProductIds = new Set();
  return items.filter((item) => {
    const productId = item.dataset.productId;
    const price = cardPriceCents(item);
    if (productId && seenProductIds.has(productId)) return false;
    if (productId) seenProductIds.add(productId);
    if (price == null) return false;
    if (minCents != null && price < minCents) return false;
    if (maxCents != null && price > maxCents) return false;
    return true;
  }).length;
}

function updateDisplayedPriceResultsUI(section, count) {
  updateDisplayedPriceCount(section, count);
  updateDisplayedPriceEmptyState(section, count);
  updateFacetsSeeResultsButtons(section, count);
}

function previewDisplayedPriceResultCount(sectionId, minAmount, maxAmount) {
  const items = displayedPriceItemsCache.get(sectionId);
  const section = sectionElement(sectionId);
  if (!section) return;

  const minCents = priceInputToCents(String(minAmount));
  const maxCents = priceInputToCents(String(maxAmount));
  if (items?.length) {
    updateDisplayedPriceResultsUI(section, countDisplayedPriceItems(items, minCents, maxCents));
    return;
  }

  // First interaction: prefetch so the drawer count can update immediately.
  const url = new URL(window.location.href);
  const sourceUrl = sourceURLForDisplayedPriceControls(url);
  fetchDisplayedPriceItems(sectionId, sourceUrl)
    .then((fetched) => {
      if (!fetched?.length) return;
      updateDisplayedPriceResultsUI(section, countDisplayedPriceItems(fetched, minCents, maxCents));
    })
    .catch(() => {});
}

function priceInputToCents(value) {
  if (value == null || value === '') return null;
  const amount = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(amount)) return null;
  return Math.round(amount * 100);
}

function cardPriceCents(item) {
  const raw = item.getAttribute('data-card-display-price-cents');
  if (raw == null || raw.trim() === '') return null;
  const price = Number(raw.trim());
  return Number.isFinite(price) ? price : null;
}

function sectionElement(sectionId) {
  return document.getElementById(`shopify-section-${sectionId}`) || document.getElementById(sectionId);
}

function gridItemsFromSectionHTML(html, sectionId) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const section = fragment.getElementById(`shopify-section-${sectionId}`);
  const roots = section ? [section, fragment] : [fragment];

  for (const root of roots) {
    const items = Array.from(root.querySelectorAll('.product-grid__item[data-card-display-price-cents]'));
    if (items.length) return items;
  }

  return [];
}

function updateDisplayedPriceCount(section, count) {
  const start = count > 0 ? 1 : 0;
  const end = count;
  section.querySelectorAll('[data-count-wrapper]').forEach((countEl) => {
    const template = countEl.dataset.countTemplate;
    countEl.dataset.countStart = String(start);
    countEl.dataset.countEnd = String(end);
    countEl.dataset.countTotal = String(count);
    if (template) {
      countEl.textContent = template
        .replace('__START__', String(start))
        .replace('__END__', String(end))
        .replace('__TOTAL__', String(count));
    }
  });
}

/**
 * Products carry an inline CSS `order` so collection ads can interleave with them, and it
 * outranks DOM order in the grid. The normalized set has no ads, so let DOM order decide.
 * @param {Element[]} items
 */
function clearGridItemOrder(items) {
  for (const item of items) {
    if (item instanceof HTMLElement) item.style.removeProperty('order');
  }
}

function disableDisplayedPricePagination(resultsList, grid) {
  grid.dataset.lastPage = '1';
  resultsList.querySelectorAll(
    '[ref="viewMorePrevious"], [ref="viewMoreNext"], .pagination-wrapper, nav.pagination, pagination-controls',
  ).forEach((element) => element.remove());
}

function sourceURLForDisplayedPriceControls(url) {
  const sourceUrl = new URL(url);
  sourceUrl.searchParams.delete(PRICE_FILTER_MIN);
  sourceUrl.searchParams.delete(PRICE_FILTER_MAX);
  if (PRICE_SORTS.has(sourceUrl.searchParams.get('sort_by') || '')) {
    sourceUrl.searchParams.delete('sort_by');
  }
  sourceUrl.searchParams.delete('page');
  return sourceUrl;
}

async function fetchDisplayedPriceItems(sectionId, sourceUrl) {
  const normalizedSourceUrl = sourceURLForDisplayedPriceControls(sourceUrl);
  const cacheKey = normalizedSourceUrl.href;
  const cached = displayedPriceItemsCacheByUrl.get(sectionId);
  if (cached?.href === cacheKey && cached.items?.length) {
    displayedPriceItemsCache.set(sectionId, cached.items);
    return cached.items;
  }

  const firstPageHTML = await sectionRenderer.getSectionHTML(sectionId, false, normalizedSourceUrl);
  const firstPageItems = gridItemsFromSectionHTML(firstPageHTML, sectionId);
  const firstPageDoc = new DOMParser().parseFromString(firstPageHTML, 'text/html');
  const firstPageGrid = firstPageDoc.querySelector('.product-grid');
  const totalPages = Number(firstPageGrid?.dataset.lastPage || 1);
  const pagePromises = [];

  for (let page = 2; page <= totalPages; page += 1) {
    const pageUrl = new URL(normalizedSourceUrl);
    pageUrl.searchParams.set('page', String(page));
    pagePromises.push(sectionRenderer.getSectionHTML(sectionId, false, pageUrl));
  }

  const laterPageItems = (await Promise.all(pagePromises))
    .flatMap((html) => gridItemsFromSectionHTML(html, sectionId));

  const allItems = [...firstPageItems, ...laterPageItems];
  displayedPriceItemsCache.set(sectionId, allItems);
  displayedPriceItemsCacheByUrl.set(sectionId, { href: cacheKey, items: allItems });
  return allItems;
}

function nativePriceFacetRangeMaxCents(section) {
  const raw = section.querySelector('[data-range-max]')?.getAttribute('data-range-max');
  const cents = Number(raw);
  return Number.isFinite(cents) ? cents : 0;
}

/**
 * Recomputes the price facet's highest-price label and slider bounds from displayed
 * card prices. Uses cache/DOM synchronously, then refines from a full fetch when needed.
 */
async function refreshDisplayedPriceFacetRange(sectionId, url = new URL(window.location.href)) {
  const section = sectionElement(sectionId);
  if (!section?.querySelector('price-facet-component')) return;

  restoreDisplayedPriceFacetRangeSync(sectionId, url);

  const sourceUrl = sourceURLForDisplayedPriceControls(url);
  let fetchedItems = [];

  try {
    fetchedItems = await fetchDisplayedPriceItems(sectionId, sourceUrl);
  } catch (error) {
    console.warn('[facets] displayed price facet range refresh failed:', error);
    return;
  }

  const rangeMax = displayedPriceRangeMaxCents(fetchedItems, section);
  if (rangeMax > 0) {
    updateDisplayedPriceFacetRange(section, rangeMax, url, sectionId);
  }
}

async function normalizeDisplayedPriceResults(sectionId) {
  const url = new URL(window.location.href);
  if (!hasDisplayedPriceControls(url)) return;

  const section = sectionElement(sectionId);
  const resultsList = section?.querySelector('results-list');
  const grid = resultsList?.querySelector('.product-grid');
  if (!section || !resultsList || !grid) return;

  // Claim this run's generation and snapshot the URL it's normalizing for. Both are
  // re-checked after the (awaited) fetch so a newer submit — or any URL change — that
  // started while pages were loading discards this run instead of applying stale results.
  const generation = (priceNormalizationRuns.get(sectionId) || 0) + 1;
  priceNormalizationRuns.set(sectionId, generation);
  const requestedHref = displayedPriceControlHref(url);

  const minCents = priceInputToCents(url.searchParams.get(PRICE_FILTER_MIN));
  const maxCents = priceInputToCents(url.searchParams.get(PRICE_FILTER_MAX));
  const sourceUrl = sourceURLForDisplayedPriceControls(url);
  const seenProductIds = new Set();

  let fetchedItems;
  try {
    fetchedItems = await fetchDisplayedPriceItems(sectionId, sourceUrl);
  } catch (error) {
    console.error('[facets] price normalization failed:', error);
    return;
  }

  // A newer submit superseded this run, or the URL changed mid-fetch — drop the stale result.
  if (
    priceNormalizationRuns.get(sectionId) !== generation ||
    displayedPriceControlHref() !== requestedHref
  ) {
    return;
  }

  let items = fetchedItems.filter((item) => {
    const productId = item.dataset.productId;
    const price = cardPriceCents(item);
    if (productId && seenProductIds.has(productId)) return false;
    if (productId) seenProductIds.add(productId);
    if (price == null) return false;
    if (minCents != null && price < minCents) return false;
    if (maxCents != null && price > maxCents) return false;
    return true;
  });

  const rangeMax = displayedPriceRangeMaxCents(fetchedItems, section);
  if (rangeMax > 0) {
    updateDisplayedPriceFacetRange(section, rangeMax, url, sectionId);
  }

  const sortBy = url.searchParams.get('sort_by');
  if (PRICE_SORTS.has(sortBy || '')) {
    items = items
      .map((item, index) => ({ item, index, price: cardPriceCents(item) || 0 }))
      .sort((a, b) => {
        const direction = sortBy === 'price-descending' ? -1 : 1;
        return (a.price - b.price) * direction || a.index - b.index;
      })
      .map(({ item }) => item);
  }

  clearGridItemOrder(items);
  grid.replaceChildren(...items);
  disableDisplayedPricePagination(resultsList, grid);
  updateDisplayedPriceResultsUI(section, items.length);

  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.has('page')) {
    currentUrl.searchParams.delete('page');
    history.replaceState({}, '', displayedPriceControlHref(currentUrl));
    rememberRenderedFacetState();
  }

  document.dispatchEvent(new CustomEvent('theme:section:morphed', {
    detail: { morphedElement: grid, sectionId },
  }));

  syncDisplayedFacetControlsFromUrl(section, url);
}

function needsDisplayedPriceRangeRefresh(sectionId) {
  const section = sectionElement(sectionId);
  if (!section?.querySelector('price-facet-component')) return false;
  if (displayedPriceRangeMaxCache.get(sectionId)) return false;
  return nativePriceFacetRangeMaxCents(section) === 0;
}

async function normalizeDisplayedPriceResultsForPage() {
  const resultsLists = document.querySelectorAll('results-list[section-id]');
  const pageUrl = new URL(window.location.href);
  for (const resultsList of resultsLists) {
    const sectionId = resultsList.getAttribute('section-id');
    if (!sectionId) continue;
    restoreDisplayedPriceFacetRangeSync(sectionId, pageUrl);
    if (hasDisplayedPriceControls(pageUrl)) {
      await normalizeDisplayedPriceResults(sectionId);
    } else if (needsDisplayedPriceRangeRefresh(sectionId)) {
      await refreshDisplayedPriceFacetRange(sectionId, pageUrl);
    }
  }
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /** @type {Promise<void>} */
  #pendingSectionUpdate = Promise.resolve();

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    newParameters = preserveFilterParametersForSorting(newParameters, this.refs.facetsForm);

    return preserveSortParameter(newParameters);
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * The element that listens for enrollment:filter:update: <enrollment-modal> on the
   * enrollment page, <loyalty-builder> on the Build your Loyalty Order page. Dispatching
   * on `document` instead reaches neither — the event would have to travel down, not up.
   * @returns {Element | null} The host element, or null outside both surfaces
   */
  #enrollmentHost() {
    return this.closest('enrollment-modal, loyalty-builder');
  }

  /**
   * Checks if this facets form is inside the enrollment modal context
   * @returns {boolean} True if inside enrollment modal
   */
  #isEnrollmentContext() {
    return this.sectionId === 'enrollment-filters' || !!this.#enrollmentHost();
  }

  /**
   * Gets the enrollment filter URL for fetching products
   * @returns {string} The filter URL
   */
  #getEnrollmentFilterUrl() {
    const urlParameters = this.createURLParameters();
    const formAction = this.refs.facetsForm.getAttribute('action') || '';
    
    // Build the URL with view parameter for enrollment results
    const url = new URL(formAction, window.location.origin);
    url.search = '';
    
    // Add view parameter for enrollment template
    url.searchParams.set('view', 'enroll-filter-results');
    
    // Add all filter parameters
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }
    
    return url.toString();
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    // Skip URL update for enrollment context (modal filters shouldn't change page URL)
    if (!this.#isEnrollmentContext()) {
      this.#updateURLHash();
    }
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#pendingSectionUpdate = this.#updateSection();
  };

  /**
   * Re-renders for the URL back/forward already moved to, without pushing a new entry.
   */
  renderFromHistory() {
    if (this.#isEnrollmentContext()) return;
    this.dispatchEvent(new FilterUpdateEvent(new URL(window.location.href).searchParams));
    // The form's inputs still hold the state being left, so they need the server's copy too.
    this.#pendingSectionUpdate = this.#updateSection({ render: true });
  }

  /**
   * Waits for the in-flight section render + displayed-price normalization pass.
   * @returns {Promise<void>}
   */
  waitForPendingUpdate() {
    return this.#pendingSectionUpdate;
  }

  /**
   * Updates the section
   * @param {{ render?: boolean }} [options] - `render` morphs even under displayed-price
   *   controls, so pills, counts and inputs follow the URL; normalization still owns the grid
   * @returns {Promise<void>}
   */
  #updateSection({ render = false } = {}) {
    // Check if we're in enrollment context
    if (this.#isEnrollmentContext()) {
      this.#updateEnrollmentSection();
      return Promise.resolve();
    }

    const browserUrl = new URL(window.location.href);
    const useDisplayedPrice = hasDisplayedPriceControls(browserUrl);

    // Morphing resets the price facet to Shopify's native range_max (often $0 for
    // displayed card prices). Client-side normalization owns the grid and facet UI.
    const renderPromise = useDisplayedPrice && !render
      ? Promise.resolve()
      : sectionRenderer.renderSection(this.sectionId);

    return renderPromise
      .then(async () => {
        // After the render, so a failed fetch leaves back/forward free to retry this state.
        rememberRenderedFacetState();
        restoreDisplayedPriceFacetRangeSync(this.sectionId, browserUrl);
        if (needsDisplayedPriceRangeRefresh(this.sectionId)) {
          await refreshDisplayedPriceFacetRange(this.sectionId, browserUrl);
        }
        await normalizeDisplayedPriceResults(this.sectionId);
        syncDisplayedFacetsFromBrowserUrl(this.sectionId);
      })
      .catch((error) => {
        console.error('[facets] section render/normalize failed:', error);
      });
  }

  /**
   * Updates the enrollment section by dispatching event to enrollment modal
   * and refreshing the filter UI to show disabled states
   */
  #updateEnrollmentSection() {
    const filterUrl = this.#getEnrollmentFilterUrl();
    
    // Also update the filter UI to reflect available combinations
    this.#updateEnrollmentFilterUI();
    
    // Dispatch custom event that the enrollment modal / loyalty builder listens to
    const host = this.#enrollmentHost() ?? document;
    host.dispatchEvent(new CustomEvent('enrollment:filter:update', {
      bubbles: true,
      detail: { filterUrl }
    }));
  }

  /**
   * Fetches and updates the enrollment filter UI to reflect which filter combinations are available
   */
  async #updateEnrollmentFilterUI() {
    const urlParameters = this.createURLParameters();
    const formAction = this.refs.facetsForm.getAttribute('action') || '';
    
    // Update filter count immediately (doesn't need to wait for fetch)
    this.#updateEnrollmentFilterCount();
    
    // Build URL to fetch updated filter HTML (without view parameter to get full page with filters)
    const url = new URL(formAction, window.location.origin);
    url.search = '';
    
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }
    
    try {
      const response = await fetch(url.toString());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the filters in the fetched page and update disabled states
      const newFilters = doc.querySelectorAll('.facets__inputs-list-item input');
      
      newFilters.forEach((newInput) => {
        if (!(newInput instanceof HTMLInputElement)) return;
        
        const name = newInput.name;
        const value = newInput.value;
        const isDisabled = newInput.disabled;
        
        // Find corresponding input in current filter UI
        const currentInput = this.querySelector(`input[name="${name}"][value="${value}"]`);
        if (currentInput instanceof HTMLInputElement) {
          currentInput.disabled = isDisabled;
          
          // Update parent element styling for disabled state
          const listItem = currentInput.closest('.facets__inputs-list-item');
          if (listItem) {
            listItem.classList.toggle('is-disabled', isDisabled);
          }
          
          // Update variant option disabled state if it's a swatch/image filter
          const variantOption = currentInput.closest('.variant-option');
          if (variantOption) {
            variantOption.classList.toggle('variant-option--swatches-disabled', isDisabled);
          }
          
          // Update tabindex on checkbox wrapper for keyboard accessibility
          const checkboxWrapper = currentInput.closest('.checkbox');
          if (checkboxWrapper) {
            checkboxWrapper.setAttribute('tabindex', isDisabled ? '-1' : '0');
            checkboxWrapper.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
          }
        }
      });
      
      // Note: Product count in "See Items" button is updated by enrollment-modal.ts
      // after fetchFinalProducts() completes with the actual filtered count
    } catch (error) {
      console.error('Failed to update enrollment filter UI:', error);
    }
  }

  /**
   * Updates the filter count display for enrollment filters
   * Shows "X filter(s) selected" text and updates the filter toggle button bubble
   */
  #updateEnrollmentFilterCount() {
    // Count all checked filter inputs (excluding price range which is handled differently)
    const checkedInputs = this.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
    let totalActiveValues = checkedInputs.length;
    
    // Also check for price range filters
    const minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    const maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');
    if ((minPriceInput instanceof HTMLInputElement && minPriceInput.value) || 
        (maxPriceInput instanceof HTMLInputElement && maxPriceInput.value)) {
      totalActiveValues += 1;
    }
    
    // Update the filter count bubble on the filter toggle button
    const enrollmentModal = this.closest('enrollment-modal');
    if (enrollmentModal) {
      const filterCountBubble = enrollmentModal.querySelector('.filter-count-bubble');
      
      if (filterCountBubble) {
        if (totalActiveValues > 0) {
          filterCountBubble.classList.remove('hidden');
          filterCountBubble.textContent = totalActiveValues.toString();
        } else {
          filterCountBubble.classList.add('hidden');
        }
      }
      
      // Update the "X filter(s) selected" text inside the drawer
      const filtersSelectedText = this.querySelector('.filters-selected-text');
      if (filtersSelectedText) {
        if (totalActiveValues > 0) {
          filtersSelectedText.classList.remove('hidden');
          // Get the translation strings from data attributes or use defaults
          const oneFilterText = filtersSelectedText.getAttribute('data-one-filter-text') || '1 filter selected';
          const multipleFiltersText = filtersSelectedText.getAttribute('data-multiple-filters-text') || '{count} filters selected';
          
          if (totalActiveValues === 1) {
            filtersSelectedText.textContent = oneFilterText;
          } else {
            filtersSelectedText.textContent = multipleFiltersText.replace('__COUNT__', totalActiveValues.toString());
          }
        } else {
          filtersSelectedText.classList.add('hidden');
        }
      }
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   * @param {{ render?: boolean }} [options] - Passed to the section update; see #updateSection
   */
  updateFiltersByURL(url, options) {
    // For enrollment context, we need to handle clearing differently
    if (this.#isEnrollmentContext()) {
      // Clear all form inputs
      this.#clearAllFormInputs();
      
      // Update filter count and UI
      this.#updateEnrollmentFilterCount();
      this.#updateEnrollmentFilterUI();
      
      // Build the clear URL with view parameter
      const clearUrl = new URL(url, window.location.origin);
      clearUrl.searchParams.set('view', 'enroll-filter-results');
      
      // Dispatch event to the enrollment modal / loyalty builder to fetch products
      const host = this.#enrollmentHost() ?? document;
      host.dispatchEvent(new CustomEvent('enrollment:filter:update', {
        bubbles: true,
        detail: { filterUrl: clearUrl.toString() }
      }));
      return;
    }
    
    history.pushState('', '', url);
    const targetUrl = new URL(url, window.location.origin);

    // Sync facet UI from the target URL immediately — form inputs still hold stale
    // price values until we do, and any section morph only lands after its fetch.
    syncDisplayedFacetsFromBrowserUrl(this.sectionId);
    sectionElement(this.sectionId)?.querySelectorAll('price-facet-component').forEach((facet) => {
      if (facet instanceof PriceFacetComponent) {
        facet.commitPendingPriceFilter();
      }
    });

    this.dispatchEvent(new FilterUpdateEvent(targetUrl.searchParams));
    this.#pendingSectionUpdate = this.#updateSection(options);
  }

  /**
   * Clears all form inputs (checkboxes, radios, text inputs)
   */
  #clearAllFormInputs() {
    // Uncheck all checkboxes and radios
    this.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
      }
    });
    
    // Clear price range inputs
    this.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = '';
      }
    });
    
    // Update facet status summaries to clear them
    this.querySelectorAll('facet-status-component').forEach((statusComponent) => {
      const facetStatus = statusComponent.querySelector('[ref="facetStatus"]');
      if (facetStatus) {
        facetStatus.innerHTML = '';
        facetStatus.classList.remove('bubble', 'facets__bubble');
      }
    });

    // Emptying the two price fields leaves the thumbs where they were, and leaves
    // data-min/data-max narrowed to the cleared range — the facet re-derives both.
    this.querySelectorAll('price-facet-component').forEach((facet) => {
      if (facet instanceof PriceFacetComponent) facet.commitPendingPriceFilter();
    });
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);

  document.addEventListener('facets:rendered', (event) => {
    if (event instanceof CustomEvent) renderedFacetState = facetStateKey(new URL(event.detail.href));
  });

  // Filter changes push history entries; without this, Back changed the URL but not the grid.
  window.addEventListener('popstate', () => {
    const state = facetStateKey(new URL(window.location.href));
    if (state === renderedFacetState) return;

    const rendered = new Set();
    document.querySelectorAll('facets-form-component').forEach((form) => {
      const sectionId = form.getAttribute('section-id');
      if (!(form instanceof FacetsFormComponent) || !sectionId || rendered.has(sectionId)) return;
      rendered.add(sectionId);
      form.renderFromHistory();
    });
  });
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} [event] - Optional event to stop propagation
   */
  updateFilters(event) {
    if (event) {
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    hidePendingHighestPriceLabels(this.closest('.shopify-section') || document);
    this.#ensureDisplayedPriceRange();
    this.#syncSliderFromInputs();
  }

  /**
   * Fetches displayed card-price range when Shopify's native range_max is 0.
   */
  #ensureDisplayedPriceRange() {
    const section = this.closest('.shopify-section');
    const sectionId = section?.querySelector('results-list[section-id]')?.getAttribute('section-id');
    if (!sectionId || displayedPriceRangeMaxCache.get(sectionId)) return;
    if (!needsDisplayedPriceRangeRefresh(sectionId)) return;
    refreshDisplayedPriceFacetRange(sectionId, new URL(window.location.href));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Cancels any debounced submit and syncs slider/input state without submitting.
   */
  commitPendingPriceFilter() {
    this.#debouncedUpdate.cancel();
    const { minInput, maxInput } = this.refs;
    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#syncSliderFromInputs();
    this.#updateSummary();
    this.#setMinAndMaxValues();
  }

  /**
   * A range spanning the whole facet excludes nothing, so it reads as no selection —
   * the same rule the PV facet applies. Left in place, gte=0&lte=max keeps a chip, a
   * summary and the displayed-price path alive for a filter that filters nothing.
   * @returns {boolean} Whether the fields were cleared
   */
  #clearFullRange() {
    const { minInput, maxInput } = this.refs;
    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return false;
    if (!minInput.value.trim() && !maxInput.value.trim()) return false;

    // data-max on the max field is read-only, unlike the pair #setMinAndMaxValues narrows.
    const rangeMax = Number(formatMoney(maxInput.getAttribute('data-max') ?? ''));
    if (!Number.isFinite(rangeMax) || rangeMax <= 0) return false;

    const min = minInput.value.trim() === '' ? 0 : Number(formatMoney(minInput.value));
    const max = maxInput.value.trim() === '' ? rangeMax : Number(formatMoney(maxInput.value));
    if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
    if (min > 0 || max < rangeMax) return false;

    minInput.value = '';
    maxInput.value = '';
    return true;
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#clearFullRange();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#syncSliderFromInputs();
  }

  /**
   * Handles input on range sliders to sync with text inputs and update filters
   * @param {InputEvent} event
   */
  handleRangeInput(event) {
    const { minInput, maxInput } = this.refs;
    const minRange = /** @type {HTMLInputElement} */ (this.refs.minRange);
    const maxRange = /** @type {HTMLInputElement} */ (this.refs.maxRange);
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    let min = Math.max(0, parseInt(minRange.value || '0', 10));
    let max = parseInt(maxRange.value || min.toString(), 10);

    if (min > max) {
      if (event?.target === minRange) {
        min = max;
        minRange.value = String(min);
      } else {
        max = min;
        maxRange.value = String(max);
      }
    }

    // Reflect into text inputs
    if (minInput instanceof HTMLInputElement) minInput.value = String(min);
    if (maxInput instanceof HTMLInputElement) maxInput.value = String(max);

    // Update active track fill and URL/results
    this.#updateSliderTrack(min, max);
    this.#previewResultCount(min, max);
    // Debounced filter update for smoother sliding
    this.#debouncedUpdate();
    this.#updateSummary();
    this.#setMinAndMaxValues();
  }

  /**
   * Updates the See-results count while the shopper adjusts the slider.
   * @param {number} min
   * @param {number} max
   */
  #previewResultCount(min, max) {
    const section = this.closest('.shopify-section');
    const sectionId = section?.querySelector('results-list[section-id]')?.getAttribute('section-id');
    if (!sectionId) return;
    previewDisplayedPriceResultCount(sectionId, min, max);
  }

  /** @type {ReturnType<typeof debounce>} */
  #debouncedUpdate = debounce(() => {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // handleRangeInput already wrote the summary for the range it typed into the fields.
    if (this.#clearFullRange()) {
      this.#setMinAndMaxValues();
      this.#updateSummary();
    }

    facetsForm.updateFilters();
  }, 250);

  /**
   * Updates slider CSS vars from current inputs
   */
  #syncSliderFromInputs() {
    const minRange = /** @type {HTMLInputElement} */ (this.refs.minRange);
    const maxRange = /** @type {HTMLInputElement} */ (this.refs.maxRange);
    const { minInput, maxInput } = this.refs;
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    const maxRangeMax = parseInt(maxRange.getAttribute('max') || '0', 10);
    if (maxRangeMax <= 0) return;

    // An empty field is no bound, so the thumb belongs at the end of the track. Falling
    // back to the thumb's own value instead would strand it wherever clearing left it.
    const min = priceFacetSliderDollars(minInput?.value, 0);
    const max = priceFacetSliderDollars(maxInput?.value, maxRangeMax);

    const clampedMin = Math.max(0, Math.min(min, maxRangeMax));
    const clampedMax = Math.min(maxRangeMax, Math.max(max, clampedMin));

    minRange.value = String(clampedMin);
    maxRange.value = String(clampedMax);
    this.#updateSliderTrack(clampedMin, clampedMax);
  }

  /**
   * Updates slider active track CSS variables
   * @param {number} min
   * @param {number} max
   */
  #updateSliderTrack(min, max) {
    const slider = /** @type {HTMLElement} */ (this.refs.slider);
    const maxRange = /** @type {HTMLInputElement} */ (this.refs.maxRange);
    if (!(slider instanceof HTMLElement) || !(maxRange instanceof HTMLInputElement)) return;
    const rangeMax = parseInt(maxRange.getAttribute('max') || '0', 10) || 1;
    const minPct = Math.max(0, Math.min(100, (min / rangeMax) * 100));
    const maxPct = Math.max(0, Math.min(100, (max / rangeMax) * 100));
    slider.style.setProperty('--min', `${minPct}%`);
    slider.style.setProperty('--max', `${maxPct}%`);
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.stopPropagation();
    event.stopImmediatePropagation();

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const priceFacet = this.closest('price-facet-component');
      if (priceFacet) {
        const url = new URL(window.location.href);
        const hasActivePrice = url.searchParams.has(PRICE_FILTER_MIN) ||
          url.searchParams.has(PRICE_FILTER_MAX);
        clearButton.classList.toggle('facets__clear--active', hasActivePrice);
        return;
      }
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Under price sorting nothing else re-renders the pills, so a server-rendered one would stay.
    facetsForm.updateFiltersByURL(url, { render: true });

    // const filtersDrawer = document.querySelector('#filters-drawer');
    // if (filtersDrawer) {
    //   setTimeout(() => {
    //     !this.classList.contains('pills__pill') && filtersDrawer?.closeDialog();
    //   }, 100);
    // }
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    normalizeDisplayedPriceResultsForPage();
  }, { once: true });
} else {
  normalizeDisplayedPriceResultsForPage();
}

/**
 * Mobile drawer "See X items" must commit the price range and wait for client-side
 * normalization before closing — otherwise the button label and PLP stay stale.
 * @param {MouseEvent} event
 */
async function handleFacetsSeeResultsClick(event) {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('.facets__see-results');
  if (!button) return;

  const section = button.closest('.shopify-section');
  const facetsForm = section?.querySelector('facets-form-component');
  if (!(facetsForm instanceof FacetsFormComponent)) return;
  if (!section?.querySelector('price-facet-component')) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const parametersBeforeCommit = facetsForm.createURLParameters().toString();

  section.querySelectorAll('price-facet-component').forEach((facet) => {
    if (facet instanceof PriceFacetComponent) {
      facet.commitPendingPriceFilter();
    }
  });

  // Re-rendering when the commit changed nothing throws away what the client-side filters
  // had already assembled, and the shopper watches the grid rebuild itself for no reason.
  if (facetsForm.createURLParameters().toString() !== parametersBeforeCommit) {
    facetsForm.updateFilters();
    await facetsForm.waitForPendingUpdate();
  }

  const dialog = button.closest('dialog-component');
  if (dialog && typeof dialog.closeDialog === 'function') {
    await dialog.closeDialog();
  }
}

document.addEventListener('click', handleFacetsSeeResultsClick, true);

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

export { hasDisplayedPriceControls, normalizeDisplayedPriceResults, rememberRenderedFacetState };
